import { useEffect, useState } from "react";
import { Download, X, Share, Plus, MoreVertical } from "lucide-react";

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

const BADGE = "flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand/10 text-xs font-bold text-brand";

export function InstallPWA() {
  const [deferred, setDeferred] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onBip = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); setShowModal(false); };
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const standalone = typeof window !== "undefined" &&
    ((window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) || window.navigator.standalone);
  if (installed || standalone) return null;

  const onClick = async () => {
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch (e) { /* ignore */ }
      setDeferred(null);
    } else {
      setShowModal(true);
    }
  };

  const ios = isIOS();

  return (
    <>
      <button onClick={onClick} data-testid="install-pwa-button"
        className="mb-2 flex w-full items-center gap-3 rounded-lg bg-white/10 px-3 py-2.5 text-sm font-medium text-white transition-colors hover:bg-white/20">
        <Download size={18} /> Install app
      </button>

      {showModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowModal(false)} />
          <div data-testid="install-instructions-modal" className="relative z-10 w-full max-w-md rounded-xl bg-white p-6 text-ink shadow-xl">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold">Install Brightside</h2>
              <button onClick={() => setShowModal(false)} data-testid="install-modal-close"><X size={20} className="text-ink-muted" /></button>
            </div>
            <p className="mb-5 text-sm text-ink-soft">Add Brightside to your home screen to open it like a normal app — no app store needed.</p>

            {ios ? (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-muted">On iPhone / iPad (Safari)</p>
                <ol className="space-y-3 text-sm text-ink">
                  <li className="flex gap-3"><span className={BADGE}>1</span><span>Tap the <span className="font-semibold">Share</span> icon <Share size={14} className="inline -mt-0.5" /> at the bottom of Safari.</span></li>
                  <li className="flex gap-3"><span className={BADGE}>2</span><span>Scroll down and tap <span className="font-semibold">Add to Home Screen</span> <Plus size={14} className="inline -mt-0.5" />.</span></li>
                  <li className="flex gap-3"><span className={BADGE}>3</span><span>Tap <span className="font-semibold">Add</span> — Brightside appears on your home screen.</span></li>
                </ol>
              </div>
            ) : (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-muted">On Android / Desktop (Chrome &amp; Edge)</p>
                <ol className="space-y-3 text-sm text-ink">
                  <li className="flex gap-3"><span className={BADGE}>1</span><span>Open the browser menu <MoreVertical size={14} className="inline -mt-0.5" /> (top-right).</span></li>
                  <li className="flex gap-3"><span className={BADGE}>2</span><span>Tap <span className="font-semibold">Install app</span> or <span className="font-semibold">Add to Home screen</span>.</span></li>
                  <li className="flex gap-3"><span className={BADGE}>3</span><span>Confirm — Brightside installs like a normal app.</span></li>
                </ol>
                <p className="mt-4 rounded-lg bg-ivory p-3 text-xs text-ink-muted">On iPhone/iPad, open this site in <span className="font-semibold">Safari</span>, then use Share → Add to Home Screen.</p>
              </div>
            )}

            <button onClick={() => setShowModal(false)} className="mt-6 w-full rounded-lg bg-brand py-2.5 font-semibold text-white transition-colors hover:bg-brand-dark">Got it</button>
          </div>
        </div>
      )}
    </>
  );
}
